// src/features/hotels/components/HotelEdit.tsx

import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  required,
  minLength,
  maxLength,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
  SaveButton,
  Toolbar,
  type AutocompleteInputProps,
} from 'react-admin';
import type { ComponentType } from 'react';
import {
  Hotel as HotelIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { RegistryLinkInput } from '@/components/forms/RegistryLinkInput';
import { NotifierConfigSection } from './NotifierConfigSection';

// Cast around a React-Admin/React 18 JSX return-type mismatch (TS2786).
const FormAutocompleteInput = AutocompleteInput as ComponentType<AutocompleteInputProps>;

/**
 * Copy ID button for the read-only ID field
 */
const CopyIdButton = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Tooltip title="Copiar ID">
      <IconButton
        size="small"
        onClick={() => navigator.clipboard.writeText(String(record.id))}
        sx={{ p: 0.5 }}
      >
        <CopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Validation rules for hotel editing
 */
const validateHotelName = [
  required('resources.hotels.validation.name_required'),
  minLength(3, 'resources.hotels.validation.name_min_length'),
  maxLength(100, 'resources.hotels.validation.name_max_length'),
];

/**
 * Custom toolbar for the hotel basic information section
 */
const HotelBasicInfoToolbar = () => {
  const translate = useTranslate();

  return (
    <Toolbar
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: 'transparent',
      }}
    >
      <Box>
        <SaveButton 
          label={translate('ra.action.save')}
          icon={<SaveIcon />}
        />
      </Box>
    </Toolbar>
  );
};

/**
 * Hotel edit component
 * Only SuperAdmin can access this
 */
export const HotelEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = () => {
    notify('resources.hotels.messages.update_success', { type: 'success' });
    redirect('/admin-options?tab=hotels');
  };

  const onError = (error: any) => {
    const errorMessage = error?.body?.error?.message || 
                        error?.message || 
                        translate('resources.hotels.messages.update_error');
    notify(errorMessage, { type: 'error' });
  };

  return (
    <Edit
      mutationOptions={{ onSuccess, onError }}
      mutationMode="pessimistic"
      title={translate('resources.hotels.edit.title')}
      // An empty group must be sent as "" (not null): the API only removes the
      // group assignment when it receives an empty string. This makes the hotel
      // independent (no group).
      transform={(data) => ({ ...data, groupId: data.groupId || '' })}
      queryOptions={{
        refetchInterval: false,  
        refetchOnWindowFocus: false, 
        refetchOnMount: false,  
      }}
    >
      <MyFormContainer maxWidth="xl" fullWidth>
        <Box sx={{ 
          maxWidth: '1000px', 
          margin: '0 auto',
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3 
        }}>
          {/* Basic Information Section - WITH FORM - Compact Height */}
          <Box sx={{ width: '100%' }}>
            <SimpleForm toolbar={false}>
              <MyFormSection
                title={translate('resources.hotels.edit.title')}
                subtitle={translate('resources.hotels.edit.subtitle')}
                icon={<HotelIcon />}
                variant="outlined"
              >
                {/* Hotel ID - read-only, for Keycloak mapping */}
                <TextInput
                  source="id"
                  label="ID"
                  disabled
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <CopyIdButton />
                    ),
                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                  }}
                  sx={{ mb: 1 }}
                  helperText="ID interno del hotel"
                />

                {/* Horizontal layout for compact form */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  alignItems: 'start'
                }}>
                  {/* Hotel Name */}
                  <TextInput
                    source="name"
                    label={translate('resources.hotels.fields.name')}
                    validate={validateHotelName}
                    fullWidth
                  />

                  {/* Group (optional: leave empty for independent hotels) */}
                  <ReferenceInput
                    source="groupId"
                    reference="groups"
                    sort={{ field: 'name', order: 'ASC' }}
                  >
                    <FormAutocompleteInput
                      label={translate('resources.hotels.fields.groupId')}
                      optionText="name"
                      fullWidth
                      emptyValue=""
                      emptyText={translate('common.notAssigned')}
                      helperText={translate('resources.hotels.hints.groupOptional')}
                    />
                  </ReferenceInput>
                </Box>

                {/* Record of the shared registry */}
                <RegistryLinkInput
                  source="directoryId"
                  kind="property"
                  label={translate('resources.hotels.fields.directoryId')}
                />

                {/* Toolbar */}
        
                  <HotelBasicInfoToolbar />

              </MyFormSection>
            </SimpleForm>
          </Box>

          {/* Notifier Configuration Section - OUTSIDE FORM - Full Width */}
          <Box sx={{ width: '100%' }}>
            <NotifierConfigWrapper />
          </Box>
        </Box>
      </MyFormContainer>
    </Edit>
  );
};

/**
 * Wrapper component to access record context for NotifierConfigSection
 */
const NotifierConfigWrapper = () => {
  const record = useRecordContext();
  
  if (!record || !record.id) {
    return null;
  }

  // Convert id to string (React Admin Identifier can be string | number)
  const hotelId = String(record.id);

  return <NotifierConfigSection hotelId={hotelId} />;
};